import { toGenericPriceKey, toOracleKey } from '../../assets'
import { isUnearnableRate } from '../rateSanity'
import { GenericTokenList, MulticallRetryFunction } from '../../types'
import { apyToAprPercent } from '../../utils/parsing'
import { fetchAssetDecimals } from '../assetDecimals'
import {
  fetchSpectraApiMarkets,
  hasSpectraMarkets,
  isLiveSpectraMarket,
  parseCurveFee,
  parsePtRate,
  pickPool,
  sanePercent,
  spectraAddress,
  type SpectraApiMarket,
  type SpectraApiToken,
} from './api'
import { SpectraPtMarket, SpectraPtMarkets } from './types'

const SECONDS_PER_DAY = 86_400

const num = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

export interface FetchSpectraPtOptions {
  /**
   * Include markets that have already matured. **Default `false`.**
   *
   * Same rule and same reasoning as the Pendle provider: a matured PT redeems
   * at par, so its forward yield is zero, while every rate field still carries
   * the last pre-expiry value. Serving those rows puts a stale fixed APY on top
   * of an APR-sorted list for a product that no longer exists.
   *
   * The Spectra-specific caveat is that this switch may have nothing to return.
   * The upstream listing appears to drop matured markets itself, so a holder of
   * a matured Spectra PT is not servable from this source at all — unlike
   * Pendle, whose `/markets/all` carries them. That is a gap in the data, not a
   * reason to leave the filter off.
   */
  includeExpired?: boolean
  /**
   * Clock override, unix seconds. Tests only — production always judges
   * maturity against the real clock, never against a cached flag.
   */
  nowSecs?: number
}

/**
 * Token metadata resolved from the first source that has it.
 *
 * The caller's token list wins because it carries our curated `assetGroup`,
 * which is how the price store is keyed. Spectra's own listing is second, and
 * unlike Pendle's it names the UNDERLYING too — its `/pools` response embeds a
 * full token object for the PT, YT, IBT and underlying alike, so tier 2 covers
 * everything tier 1 missed and the on-chain tier is very nearly dead code.
 */
interface ResolvedToken {
  decimals?: number
  symbol?: string
  name?: string
  logoURI?: string
  assetGroup?: string
}

function resolveToken(
  address: string | undefined,
  apiToken: SpectraApiToken | null | undefined,
  tokenList: GenericTokenList,
  decimalsOnChain: Map<string, number>,
): ResolvedToken {
  const listed = address ? tokenList[address] : undefined
  const apiDecimals =
    typeof apiToken?.decimals === 'number' && Number.isFinite(apiToken.decimals)
      ? apiToken.decimals
      : undefined
  return {
    decimals:
      listed?.decimals ??
      apiDecimals ??
      (address ? decimalsOnChain.get(address) : undefined),
    symbol: listed?.symbol ?? apiToken?.symbol ?? undefined,
    name: listed?.name ?? apiToken?.name ?? undefined,
    logoURI: listed?.logoURI ?? apiToken?.logoURI ?? undefined,
    assetGroup: listed?.assetGroup,
  }
}

/**
 * One API market → a `SpectraPtMarket`, or `null` when it cannot be modelled.
 *
 * Drops rather than patches. Four conditions each drop the row, and each is a
 * case where a default would be a specific false claim rather than a gap:
 *
 *  - no PT address, no underlying, or no pool — nothing to buy;
 *  - no parseable maturity — an unbounded fixed-rate row;
 *  - PT or underlying decimals unknown — silently mis-scaled amounts;
 *  - **no believable `impliedApy`** — a fixed-rate product with no rate.
 *
 * The last one is the Spectra-specific one and it fires on real rows: Hemi
 * publishes `null` and HyperEVM publishes raw fixed point (`5.5e17`). Reporting
 * 0 % there would be a claim about the market; dropping it says we do not know.
 */
function parseMarket(
  m: SpectraApiMarket,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
  decimalsOnChain: Map<string, number>,
  nowSecs: number,
): SpectraPtMarket | null {
  const ptAddress = spectraAddress(m.address)
  const underlyingAddress = spectraAddress(m.underlying?.address)
  if (!ptAddress || !underlyingAddress) return null

  const maturity = num(m.maturity)
  if (maturity === null) return null

  const pool = pickPool(m.pools)
  if (!pool) return null

  // Defence in depth, exactly like the maturity check. A PT whose `ptRate` has
  // been written down to zero redeems for nothing — it is not a fixed-rate
  // product regardless of what its APY field says. Today the rate guard below
  // happens to catch the same three markets, but the two facts are independent
  // and the day they disagree is the day this matters.
  const ptRate = parsePtRate(m.rate)
  if (ptRate === undefined || ptRate <= 0) return null

  // `impliedApy` is the market's rate; `ptApy` is what a taker realises. See
  // both docstrings in `api.ts` — they are not interchangeable, and the one
  // that must never leak into `supplyRate` is the second.
  const impliedApyPercent = sanePercent(pool.impliedApy)
  if (impliedApyPercent === undefined) return null

  const ptMeta = resolveToken(
    ptAddress,
    // The market object IS the PT's token record — `name`/`symbol`/`decimals`
    // at the root describe the PrincipalToken, not the underlying.
    {
      address: m.address,
      name: m.name,
      symbol: m.symbol,
      decimals: m.decimals,
    },
    tokenList,
    decimalsOnChain,
  )
  const assetMeta = resolveToken(
    underlyingAddress,
    m.underlying,
    tokenList,
    decimalsOnChain,
  )

  // Never the underlying's decimals as a fallback for the PT's — they disagree
  // on 2 of 36 live markets, and the error is silent.
  const decimals = ptMeta.decimals
  const assetDecimals = assetMeta.decimals
  if (decimals === undefined || assetDecimals === undefined) return null

  // The convention is a nominal APR percent; `impliedApy` is a compounded APY
  // ALREADY IN PERCENT (Pendle's is a fraction — the one substitution most
  // likely to be made silently wrong).
  const supplyRate = apyToAprPercent(impliedApyPercent)

  const priceKey =
    toOracleKey(assetMeta.assetGroup ?? null) ||
    toGenericPriceKey(underlyingAddress, chainId)
  const priceUsd = prices[priceKey] ?? prices[underlyingAddress]

  const totalAssetsUnderlying = num(m.tvl?.underlying) ?? undefined
  // `tvl.usd` is nullable upstream. Recovering it from the underlying leg keeps
  // the row at its true magnitude instead of sorting it to the bottom of a
  // TVL-ranked list at 0 — which is the failure a caller would never notice.
  const totalAssetsUsd =
    num(m.tvl?.usd) ??
    (totalAssetsUnderlying !== undefined && priceUsd !== undefined
      ? totalAssetsUnderlying * priceUsd
      : 0)

  const liquidityUnderlying = num(pool.liquidity?.underlying)
  const liquidityUsd =
    num(pool.liquidity?.usd) ??
    (liquidityUnderlying !== null && priceUsd !== undefined
      ? liquidityUnderlying * priceUsd
      : 0)

  // The PT trades at a DISCOUNT to the underlying — that discount IS the yield
  // — so the two prices are looked up separately and never substituted.
  const ptPriceKey =
    toOracleKey(ptMeta.assetGroup ?? null) ||
    toGenericPriceKey(ptAddress, chainId)
  const ptPriceUsd =
    prices[ptPriceKey] ?? prices[ptAddress] ?? num(pool.ptPrice?.usd) ?? undefined

  const secondsToExpiry = Math.max(maturity - nowSecs, 0)

  return {
    address: ptAddress,
    marketAddress: spectraAddress(pool.address),
    underlying: underlyingAddress,
    ytAddress: spectraAddress(m.yt?.address),
    ibtAddress: spectraAddress(m.ibt?.address),
    baseIbtAddress: spectraAddress(m.baseIbt?.address),

    symbol: ptMeta.symbol ?? `PT-${assetMeta.symbol ?? ''}`,
    name: ptMeta.name ?? `Principal Token ${assetMeta.symbol ?? ''}`,
    decimals,
    assetDecimals,

    expiry: maturity,
    expiryIso: new Date(maturity * 1000).toISOString(),
    secondsToExpiry,
    daysToExpiry: Math.round((secondsToExpiry / SECONDS_PER_DAY) * 100) / 100,

    supplyRate,
    rewardsRate: 0,
    depositRate: supplyRate,
    impliedApyPercent,
    executableApyPercent: sanePercent(pool.ptApy),
    underlyingApyPercent: sanePercent(m.ibt?.apr?.total),
    feeRate: parseCurveFee(pool.feeRate),

    totalAssetsUsd,
    totalAssetsFormatted: totalAssetsUsd,
    liquidityUsd,
    totalAssetsUnderlying,

    ptRate,
    principalWriteDownBps: Math.max(0, Math.round((1 - ptRate) * 10_000)),

    isMintable: true,
    withdrawalMode: 'market-sale',

    // Pre-seeded from Spectra's own icon CDN. `stampVaultClassification`
    // overwrites it when the token list resolves something and leaves it alone
    // otherwise, so a row still renders an icon with no token list.
    logoURI: ptMeta.logoURI,

    // Prefer the curated token-list entry (it carries `assetGroup`, tags and
    // everything else downstream reads); synthesize from Spectra's own record
    // otherwise, so `stampVaultClassification` still has a symbol to read for
    // the stable/volatile call.
    asset:
      tokenList[underlyingAddress] ??
      (assetMeta.symbol
        ? {
            chainId,
            address: underlyingAddress,
            symbol: assetMeta.symbol,
            name: assetMeta.name,
            decimals: assetDecimals,
            logoURI: assetMeta.logoURI,
          }
        : undefined),
    priceUsd,
    ptPriceUsd,
    ptPriceUnderlying: num(pool.ptPrice?.underlying) ?? undefined,
    categoryIds: Array.isArray(m.tags) ? m.tags : undefined,
    protocol: m.ibt?.protocol ?? undefined,
  }
}

/**
 * Fetch every LIVE Spectra PT market on a chain, modelled as fixed-rate earn
 * products.
 *
 * **HTTP-only in every normal case, and normally ONE request.** Unlike Pendle
 * there is no global listing — the endpoint is per network — so this issues one
 * call per chain, cached in-isolate for 60 s and shared between concurrent
 * callers. Unlike Pendle there is also no second metadata endpoint to need: the
 * `/pools` response embeds decimals, symbol, name and icon for the PT, YT, IBT
 * and underlying alike, so the on-chain tier below fires only if that stops
 * being true.
 *
 * The multicall stays a last resort for the reason recorded in the Pendle
 * provider: viem's `allowFailure` returns `'0x'` per call rather than throwing
 * when the transport dies, so a decimals batch that fails does not error — it
 * silently empties a whole chain's listing.
 *
 * @param chainId         target chain
 * @param multicallRetry  last-resort decimals gap-fill only; normally unused
 * @param prices          price map keyed by oracle key / address — also what
 *                        recovers a null `tvl.usd`
 * @param tokenList       token list for PT + underlying metadata
 * @param options         `includeExpired` and a test clock
 *
 * @returns map keyed by lowercased PT address; empty on chains without a
 *          Spectra deployment.
 */
export const fetchSpectraPtMarkets = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  options: FetchSpectraPtOptions = {},
): Promise<SpectraPtMarkets> => {
  if (!hasSpectraMarkets(chainId)) return {}

  const nowSecs = options.nowSecs ?? Math.floor(Date.now() / 1000)
  const all = await fetchSpectraApiMarkets(chainId)

  const candidates = all.filter(
    (m) =>
      !!m &&
      (options.includeExpired === true || isLiveSpectraMarket(m, nowSecs)),
  )
  if (candidates.length === 0) return {}

  // Tier 3 — on-chain, for anything neither the token list nor Spectra's own
  // record names. Normally empty; kept because a dropped row is the
  // alternative and a market we can price is worth one call to keep.
  const unresolved = new Set<string>()
  for (const m of candidates) {
    const pairs: Array<[string | undefined, number | null | undefined]> = [
      [spectraAddress(m.address), m.decimals],
      [spectraAddress(m.underlying?.address), m.underlying?.decimals],
    ]
    for (const [address, apiDecimals] of pairs) {
      if (!address) continue
      if (tokenList[address]?.decimals !== undefined) continue
      if (typeof apiDecimals === 'number' && Number.isFinite(apiDecimals))
        continue
      unresolved.add(address)
    }
  }

  let decimalsOnChain = new Map<string, number>()
  if (unresolved.size > 0) {
    try {
      decimalsOnChain = await fetchAssetDecimals(
        chainId,
        [...unresolved],
        multicallRetry,
      )
    } catch {
      // A failed gap-fill costs the unresolved markets, not the resolved ones —
      // `parseMarket` drops what it cannot scale. Never default.
      decimalsOnChain = new Map()
    }
  }

  const out: SpectraPtMarkets = {}
  for (const m of candidates) {
    const parsed = parseMarket(
      m,
      chainId,
      prices,
      tokenList,
      decimalsOnChain,
      nowSecs,
    )
    if (!parsed) continue
    // A PT hours from maturity on a few dollars of TVL publishes whatever the
    // last trade rounded to, annualized by a factor of ~1,700 — see
    // `isUnearnableRate`. Dropped rather than re-rated, and only ever when
    // BOTH the size and the implied price deviation say noise, so a genuine
    // above-par PT with real money in it still reports its negative rate.
    if (
      isUnearnableRate({
        aprPercent: parsed.supplyRate,
        totalAssetsUsd: parsed.totalAssetsUsd,
        expiry: parsed.expiry,
        nowSecs,
      })
    ) {
      continue
    }
    out[parsed.address] = parsed
  }
  return out
}
